import { useEffect, useState, useRef, useCallback } from 'react'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import type { Root } from 'react-dom/client'
import { modalConfig } from 'reusable/mui/modals/modal-theme'
import { Timeouts } from '@gap/utils/date-utils'
import type { SnackbarOptions } from './snackbar'
import GapSnackbar from './GapSnackbar'
import { observer } from 'mobx-react-lite'
import { snackbarStack } from './SnackbarStack'

/** Vertical distance between stacked snackbars, in pixels. */
const STACK_SPACING_PX = 64

/**
 * Imperative lifecycle wrapper for the snackbar helpers in `./snackbar`.
 * Mounted outside the React tree via `reactRender`, so it provides its own
 * `ThemeProvider` and tears itself down on close. Rendering lives in `GapSnackbar`.
 */
export const SnackbarComponent = observer(function SnackbarComponent({
   title,
   autoHideDuration,
   variant = 'info',
   container,
   root,
   actions = [],
}: SnackbarOptions & { container: HTMLElement, root: Root }) {
   const removed = useRef(false)
   const [open, setOpen] = useState(true)
   const [stackId, setStackId] = useState<number | null>(null)

   useEffect(() => {
      return () => {
         if (removed.current) return

         document.body.removeChild(container)
         removed.current = true
      }
   }, [container])

   useEffect(() => {
      const openedStackId = snackbarStack.open()
      setStackId(openedStackId)
      return () => snackbarStack.close(openedStackId)
   }, [])

   const handleClose = useCallback(() => {
      setOpen(false)
      setTimeout(() => root.unmount())
   }, [root])

   const stackOffset = stackId === null ? 0 : snackbarStack.positionOf(stackId) * STACK_SPACING_PX

   return (
      <ThemeProvider theme={modalConfig.theme}>
         <CssBaseline />
         <GapSnackbar
            open={open}
            variant={variant}
            message={title}
            actions={actions}
            autoHideDuration={autoHideDuration || Timeouts.second * 4}
            onClose={handleClose}
            sx={{ bottom: { xs: 8 + stackOffset, sm: 24 + stackOffset } }}
         />
      </ThemeProvider>
   )
})
